/**
 * The solo microphone check, then the connection check — the last step
 * before stage 1.
 *
 * The level meter is the friends-mode `MicCheck` unchanged: 800 ms of speech
 * inside any 3 s window passes (`voice/micCheckWindow.ts`), a manual
 * "my mic works" appears after 45 s, and a pass moves on by itself a beat
 * later. What this adds is the way out: "I have a microphone problem", behind
 * a confirmation, which ends the session as a failed voice check. The
 * connection check is the friends-mode `LatencyGate`; failing it twice ends
 * the session as an incompatible device.
 */

import { useCallback, useState } from 'react'
import type { LatencyConfig } from '@overcooked/shared'
import { LatencyGate } from '../flow/LatencyGate'
import { MicCheck } from '../flow/MicCheck'
import type { MicCheckMethod } from '../flow/MicCheck'
import type { PingTransport } from '../voice/latency'

interface StudyMicCheckProps {
  transport: PingTransport | null
  latency: LatencyConfig | null
  onPass(stream: MediaStream, result: { method: MicCheckMethod; medianRttMs: number }): void
  onFail(failure: 'voice' | 'latency'): void
}

/** Failed connection checks before the participant is offered the way out. */
const LATENCY_TRIES_BEFORE_EXIT = 2

export function StudyMicCheck({ transport, latency, onPass, onFail }: StudyMicCheckProps) {
  const [stage, setStage] = useState<'mic' | 'confirm-problem' | 'latency'>('mic')
  const [mic, setMic] = useState<{ stream: MediaStream; method: MicCheckMethod } | null>(null)
  const [latencyFailures, setLatencyFailures] = useState(0)

  const onMicPass = useCallback((stream: MediaStream, method: MicCheckMethod) => {
    setMic({ stream, method })
    setStage('latency')
  }, [])
  const onLatencyPass = useCallback(
    (result: { medianMs: number }) => {
      if (mic) onPass(mic.stream, { method: mic.method, medianRttMs: Math.round(result.medianMs) })
    },
    [mic, onPass],
  )
  const onLatencyFail = useCallback(() => setLatencyFailures((n) => n + 1), [])

  if (stage === 'confirm-problem') {
    return (
      <section className="gate">
        <p className="gate-step">Microphone</p>
        <h1>Microphone problem?</h1>
        <p className="hint">
          You need a working microphone to talk to your partner. If yours doesn't work, we'll end
          the study and ask you to return your submission on Prolific.
        </p>
        <div className="gate-actions">
          <button type="button" onClick={() => setStage('mic')} data-testid="mic-problem-back">
            Try again
          </button>
          <button type="button" onClick={() => onFail('voice')} data-testid="mic-problem-confirm">
            End the study
          </button>
        </div>
      </section>
    )
  }

  if (stage === 'latency') {
    if (!transport || !latency) {
      return (
        <section className="gate">
          <h1>Connecting…</h1>
        </section>
      )
    }
    return (
      <>
        <LatencyGate transport={transport} config={latency} step={2} of={2} onPass={onLatencyPass} onFail={onLatencyFail} />
        {latencyFailures >= LATENCY_TRIES_BEFORE_EXIT && (
          <section className="gate">
            <p className="hint">If this check keeps failing, the game won't work on this connection.</p>
            <div className="gate-actions">
              <button type="button" onClick={() => onFail('latency')} data-testid="latency-give-up">
                I can't continue on this connection
              </button>
            </div>
          </section>
        )}
      </>
    )
  }

  return (
    <>
      <MicCheck step={1} of={2} onPass={onMicPass} onGiveUp={() => setStage('confirm-problem')} />
      <div className="study-mic-problem">
        <button type="button" className="ghost" onClick={() => setStage('confirm-problem')} data-testid="mic-problem">
          I have a microphone problem
        </button>
      </div>
    </>
  )
}
